import notificationService from '@/services/NotificationService';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import connectorSyncPollingService, { ConnectorSyncResult } from '@/services/ConnectorSyncPollingService';
import appStore from '@/store';
import { Logger } from '@/other/Logger';
import { ConnectorConfig } from '@/models/ConnectorConfig';
import {
  commitUpdateConnector,
  commitSetConnectorSyncState,
  commitClearConnectorSyncState,
} from '@/store/commits/connectorCommits';
import { dispatchStartConnectorSync, dispatchRefreshSyncStatuses } from '@/store/dispatchers/connectorDispatchers';

const log = new Logger('ConnectorSyncMonitor');

interface TrackOptions {
  since?: number;
}

/**
 * App-level singleton that owns the sync polling lifecycle for every connector in the workspace.
 *
 * Lifting this above any single dialog means:
 *   - The user can close the wizard / edit dialog and the sync still completes correctly
 *     (article reload + toast still fire).
 *   - On a page refresh, init() rehydrates state from the bulk /sync-statuses endpoint and
 *     resumes polling for any connector that is still in_progress, so the "Syncing…" badge
 *     in the picker survives reloads.
 *   - Components only need to call startSync(); they never own a poll handle.
 */
class ConnectorSyncMonitor {
  private trackedIds = new Set<string>();
  private isInitialized = false;

  /**
   * Boot-time hydration. Fetches sync state for every passed connector in one round trip
   * and starts polling for the ones that are mid-sync. Idempotent: safe to call multiple
   * times after workspace switches.
   */
  async init(connectorIds: string[]): Promise<void> {
    if (this.isInitialized) {
      this.dispose();
    }
    this.isInitialized = true;

    if (connectorIds.length === 0) {
      return;
    }

    try {
      const hydrated = await dispatchRefreshSyncStatuses(connectorIds);
      for (const [cid, syncStatus] of Object.entries(hydrated)) {
        if (syncStatus.status === 'in_progress') {
          this.track(cid, { since: syncStatus.since });
        }
      }
    } catch (err: unknown) {
      log.info(`Failed to hydrate connector sync states: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  /**
   * Trigger a sync and start tracking it. Components call this from save handlers
   * instead of poking the API directly.
   */
  async startSync(connectorId: string): Promise<{ jobId: string | null; alreadyRunning: boolean }> {
    const result = await dispatchStartConnectorSync(connectorId);
    this.track(connectorId, { since: result.since });
    return { jobId: result.jobId, alreadyRunning: result.alreadyRunning };
  }

  /**
   * Begin polling /sync-status for a connector. Idempotent — re-tracking an already-tracked
   * id resets the underlying poll cleanly via the polling service's cancel-and-restart.
   */
  track(connectorId: string, options: TrackOptions = {}): void {
    if (this.trackedIds.has(connectorId)) {
      connectorSyncPollingService.cancel(connectorId);
    }
    this.trackedIds.add(connectorId);

    connectorSyncPollingService
      .poll(connectorId, { since: options.since })
      .then((result) => this.handleCompletion(connectorId, result))
      .catch((err) => this.handleError(connectorId, err));
  }

  cancel(connectorId: string): void {
    connectorSyncPollingService.cancel(connectorId);
    this.trackedIds.delete(connectorId);
  }

  dispose(): void {
    for (const id of Array.from(this.trackedIds)) {
      connectorSyncPollingService.cancel(id);
    }
    this.trackedIds.clear();
    this.isInitialized = false;
  }

  private async handleCompletion(connectorId: string, result: ConnectorSyncResult): Promise<void> {
    this.trackedIds.delete(connectorId);
    commitUpdateConnector(result.connector);
    commitClearConnectorSyncState(connectorId);

    const activeWorkspaceId = appStore.state.workspaceView?.workspace?.id ?? null;
    const connectorWorkspaceId = result.connector.workspace;

    if (connectorWorkspaceId && connectorWorkspaceId === activeWorkspaceId) {
      if (result.workspaceOrder && Array.isArray(result.workspaceOrder)) {
        appStore.commit('setWorkspaceOrder', result.workspaceOrder);
      }
      try {
        await workspaceService.loadArticlesItems();
      } catch (err: unknown) {
        log.info(`Failed to reload articles after sync: ${err instanceof Error ? err.message : String(err)}`);
      }
    }

    const displayName = result.connector.name || this.fallbackNameFor(connectorId);
    notificationService.success(`Synced articles from ${displayName}`);
  }

  private handleError(connectorId: string, err: unknown): void {
    this.trackedIds.delete(connectorId);

    if (err instanceof Error && err.message === 'Sync polling cancelled') {
      // Cancellation is not an error — we initiate it on logout, workspace switch, or re-track.
      return;
    }

    const message = err instanceof Error ? err.message : 'Sync failed';
    commitSetConnectorSyncState(connectorId, { status: 'error', error: message });

    const displayName = this.fallbackNameFor(connectorId);
    notificationService.error(`Failed to sync ${displayName}: ${message}`);
    log.info(`Sync failed for ${connectorId}: ${message}`);
  }

  private fallbackNameFor(connectorId: string): string {
    const connectors = (appStore.state.connector?.connectors ?? []) as ConnectorConfig[];
    const match = connectors.find((c) => c.id === connectorId);
    return match?.name || 'integration';
  }

  /**
   * Internal accessor — exposed for components that need to know whether a sync is being
   * tracked locally without round-tripping the store. Most callers should rely on the store.
   */
  isTracking(connectorId: string): boolean {
    return this.trackedIds.has(connectorId);
  }
}

const connectorSyncMonitor = new ConnectorSyncMonitor();
export default connectorSyncMonitor;
